// @ts-nocheck
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin, Users, CreditCard } from 'lucide-react';
import { useMyBilling, formatMoney } from '@/hooks/useMyBilling';
import { usePurchaseEventRegistration } from '@/hooks/usePurchaseEventRegistration';
import { formatCalendarRange } from '@/lib/dates';

/**
 * One list of "my registrations, and what I owe", shared by the customer,
 * parent and club-coach screens.
 *
 * The amount on Pay Now is `outstanding_balance` straight from
 * rle-list-my-billing. It is never recomputed here — see useMyBilling.
 */
interface Props {
  cancelPath: string;
  emptyMessage?: string;
  onlyOutstanding?: boolean;
}

const MyRegistrationsList: React.FC<Props> = ({
  cancelPath,
  emptyMessage = 'No event registrations yet.',
  onlyOutstanding = false,
}) => {
  const { data, isLoading, error } = useMyBilling({ onlyOutstanding });
  const { purchaseEventRegistration, isProcessing } = usePurchaseEventRegistration();

  const registrations = data?.registrations ?? [];
  const summary = data?.summary;

  const handlePayNow = (reg: any) => {
    purchaseEventRegistration({
      registrationId: reg.id,
      success_url: `${window.location.origin}/event-registration-success`,
      cancel_url: `${window.location.origin}${cancelPath}`,
    });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading your registrations…</p>;
  }
  if (error) {
    return (
      <p className="text-sm text-destructive">
        We could not load your registrations: {(error as any).message}
      </p>
    );
  }
  if (registrations.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <div className="space-y-4">
      {summary && summary.total_outstanding > 0 && (
        <div className="flex flex-wrap gap-4 text-sm rounded-md border bg-muted/40 p-3">
          <span>Total due: <strong>{formatMoney(summary.total_due)}</strong></span>
          <span>Paid: <strong>{formatMoney(summary.total_paid)}</strong></span>
          <span>Outstanding: <strong>{formatMoney(summary.total_outstanding)}</strong></span>
        </div>
      )}

      {registrations.map((reg) => (
        <Card key={reg.id}>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-base">{reg.event_name}</CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-3 pt-1">
                  {reg.event_start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatCalendarRange(reg.event_start_date, reg.event_end_date)}
                    </span>
                  )}
                  {(reg.event_venue || reg.event_city) && (
                    <span className="flex items-center gap-1">
                      <MapPin className="h-3.5 w-3.5" />
                      {[reg.event_venue, reg.event_city].filter(Boolean).join(' · ')}
                    </span>
                  )}
                </CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline">{reg.registration_status ?? 'registered'}</Badge>
                <Badge variant={reg.outstanding_balance > 0 ? 'secondary' : 'default'}>
                  {reg.payment_status ?? 'unpaid'}
                </Badge>
              </div>
            </div>
          </CardHeader>

          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <span className="flex items-center gap-1 font-medium">
                <Users className="h-4 w-4" />
                {reg.team_name}
              </span>
              {reg.division && <Badge variant="outline">{reg.division}</Badge>}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <div>
                <div className="text-muted-foreground text-xs">Total</div>
                <div className="font-medium">{formatMoney(reg.total_amount_due)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Paid</div>
                <div className="font-medium">{formatMoney(reg.total_amount_paid)}</div>
              </div>
              {reg.total_amount_refunded > 0 && (
                <div>
                  <div className="text-muted-foreground text-xs">Refunded</div>
                  <div className="font-medium">{formatMoney(reg.total_amount_refunded)}</div>
                </div>
              )}
              <div>
                <div className="text-muted-foreground text-xs">Outstanding</div>
                <div className="font-semibold">{formatMoney(reg.outstanding_balance)}</div>
              </div>
            </div>

            {reg.extras?.length > 0 && (
              <div className="rounded-md border p-3">
                <div className="mb-2 flex items-center gap-1 text-sm font-medium">
                  <CreditCard className="h-4 w-4" /> Extras
                </div>
                <ul className="space-y-1 text-sm">
                  {reg.extras.map((x) => (
                    <li key={x.id} className="flex justify-between gap-3">
                      <span>
                        {x.extra_name}
                        {x.player_name ? ` — ${x.player_name}` : ''}
                      </span>
                      <span className="text-muted-foreground">{formatMoney(x.extra_price)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {reg.can_pay && (
              <Button
                className="w-full sm:w-auto"
                disabled={isProcessing}
                onClick={() => handlePayNow(reg)}
              >
                {isProcessing
                  ? 'Starting checkout…'
                  : `Pay Now — ${formatMoney(reg.outstanding_balance)}`}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default MyRegistrationsList;
